import { FC, useState, useEffect } from 'react';
import clsx from 'clsx';
import Print from '../../Print';
import styles from '../../ProductPage.module.scss';
import List from '../List/List';
import Pagination from '../../../CatalogPage/components/Pagination/Pagination';

import BackButton from '../../../../BackButton/BackButton';
import { IGetProductResponse } from '../../../../../types/products';
import { generateImageUrl } from '../../../../../helpers/generateImageUrl';

import { getDocumentUrl } from '../../../../../api/document/getDocumentUrl';
import { Link } from 'react-router-dom';

import {
  getFavorites,
  updateFavorites,
} from '../../../../../helpers/favorites';
import { ICardProps as IFavorites } from '../../../FavoritesPage/components/Card/Card';
import Favorites from '../../../../Icons/Favorites';

import { formatBits } from '../../../../../helpers/formatBits';

export interface IModel {
  type: string;
  name: string;
  commercial_number: string;
  article_number: string;
  category: string;
  date: string;
  image: string;
  brand: string;
  brand_logo: string;
  id: string;
}

const Model: FC<IGetProductResponse> = ({
  name,
  image,
  cmmf,
  commercialCode,
  brand,
  category,
  parts,
  extras,
  date,
  electricalScheme,
  repInstruction,
  techInfo,
  explodedViews,
  id,
}) => {
  const [type, setType] = useState('parts'); //accessories
  const [list, setList] = useState<any[]>([]);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(0);
  const [limit, setLimit] = useState<number>(20);
  const [remainder, setRemainder] = useState<number>(0);
  const count = 12;

  const updatePage = (page: number) => {
    if (type === 'parts' && parts) {
      setList(parts.slice(limit * (page - 1), limit * page));
      setRemainder(
        parts.length - limit * page > 0 ? parts.length - limit * page : 0,
      );
    } else if (type === 'accessories' && extras) {
      setList(extras.slice(limit * (page - 1), limit * page));
      setRemainder(
        extras.length - limit * page > 0 ? extras.length - limit * page : 0,
      );
    }
    setCurrentPage(page);
  };
  const updateLimit = (value: number) => {
    setLimit(value);
    if (type === 'parts' && parts) {
      setPages(parts.length / value > 1 ? Math.ceil(parts.length / value) : 1);
      setList(parts.slice(0, value));
      setRemainder(parts.length - value > 0 ? parts.length - value : 0);
    } else if (type === 'accessories' && extras) {
      setList(extras.slice(0, value));
      setPages(
        extras.length / value > 1 ? Math.ceil(extras.length / value) : 1,
      );
      setRemainder(extras.length - value > 0 ? extras.length - value : 0);
    }
  };
  const showMore = () => {
    if (type === 'parts' && parts) {
      setList(parts.slice(0, limit * currentPage + count));
      setRemainder((state) => parts.length - list.length - state);
    } else if (type === 'accessories' && extras) {
      setList(extras.slice(0, limit * currentPage + count));
      setRemainder((state) => extras.length - list.length - state);
    }
  };

  useEffect(() => {
    if (parts && parts.length) {
      setType('parts');
      setList(parts.slice(0, limit));
      setPages(parts.length / limit > 1 ? Math.ceil(parts.length / limit) : 1);
      setRemainder(parts.length - limit > 0 ? parts.length - limit : 0);
    } else if (extras && extras.length) {
      setType('accessories');
      setList(extras.slice(0, limit));
      setPages(
        extras.length / limit > 1 ? Math.ceil(extras.length / limit) : 1,
      );
      setRemainder(extras.length - limit > 0 ? extras.length - limit : 0);
    }
  }, [parts, extras]);

  useEffect(() => {
    if (type === 'parts' && parts && parts.length) {
      setList(parts.slice(0, 20));
      setType('parts');
      setCurrentPage(1);
      setLimit(20);
      setRemainder(parts ? (parts.length - 20 > 0 ? parts.length - 20 : 0) : 0);
      setPages(parts.length / 20 > 1 ? Math.ceil(parts.length / 20) : 1);
    } else if (type === 'accessories' && extras && extras.length) {
      setList(extras.slice(0, 20));
      setType('accessories');
      setCurrentPage(1);
      setLimit(20);
      setRemainder(
        extras ? (extras.length - 20 > 0 ? extras.length - 20 : 0) : 0,
      );
      setPages(extras.length / 20 > 1 ? Math.ceil(extras.length / 20) : 1);
    }
  }, [type]);

  const [electricalSchemeLinks, setElectricalSchemeLinks] = useState<
    { url: string; size: number }[]
  >([]);
  const [repInstructionLinks, setRepInstructionLinks] = useState<
    { url: string; size: number }[]
  >([]);
  const [techInfoLinks, setTechInfoLinks] = useState<
    { url: string; size: number }[]
  >([]);

  const [loadingDocuments, setLoadingDocuments] = useState<boolean>(true);

  useEffect(() => {
    const loadDocs = async () => {
      if (electricalScheme.length) {
        for (let item of electricalScheme) {
          if (!item.s3Bucket && !item.s3Key) continue;
          const res = await getDocumentUrl(item.s3Bucket, item.s3Key);
          setElectricalSchemeLinks((state) => [
            ...state,
            { url: res.url, size: item.size },
          ]);
        }
      }

      if (repInstruction.length) {
        for (let item of repInstruction) {
          if (!item.s3Bucket && !item.s3Key) continue;
          const res = await getDocumentUrl(item.s3Bucket, item.s3Key);
          setRepInstructionLinks((state) => [
            ...state,
            { url: res.url, size: item.size },
          ]);
        }
      }

      if (techInfo.length) {
        for (let item of techInfo) {
          if (!item.s3Bucket && !item.s3Key) continue;
          const res = await getDocumentUrl(item.s3Bucket, item.s3Key);
          setTechInfoLinks((state) => [
            ...state,
            { url: res.url, size: item.size },
          ]);
        }
      }
      setLoadingDocuments(false);
    };

    loadDocs();
  }, [electricalScheme, repInstruction, techInfo]);

  const [indexInfavorites, setIndexInFavorites] = useState<number>(-1);

  const updateFavoritesList = (data: IFavorites) => {
    if (indexInfavorites === -1) {
      updateFavorites(data, 'add').then(() => {
        setIndexInFavorites(1);
      });
    } else
      updateFavorites(data, 'delete').then(() => {
        setIndexInFavorites(-1);
      });
  };

  useEffect(() => {
    getFavorites().then((arr) =>
      setIndexInFavorites(arr.findIndex((item) => item.id === id)),
    );
  }, []);

  return (
    <div className={styles.Model}>
      <div className={styles.back_button_holder}>
        <BackButton />
      </div>
      <div className={styles.header}>
        <h1>{name}</h1>
        <button
          className={styles.favorites_btn}
          onClick={(e) => {
            e.preventDefault();
            updateFavoritesList({
              type: 'product',
              name,
              cmmf,
              brand,
              category,
              image,
              id,
              article_number: '',
              commercial_number: cmmf,
            });
          }}
        >
          <Favorites active={indexInfavorites !== -1} />
          {indexInfavorites !== -1 ? 'В избранном' : 'В избранное'}
        </button>
      </div>
      <div className={styles.info_holder}>
        <div className={styles.info}>
          <div className={styles.main}>
            <div className={styles.brand}>
              {brand && (
                <img
                  src={
                    brand.image?.s3Key
                      ? generateImageUrl(brand.image?.s3Key)
                      : '/images/placeholderImage.png'
                  }
                  alt="brand"
                />
              )}
            </div>
            <div className={styles.content}>
              {cmmf && (
                <span>
                  Артикул: <b>{cmmf}</b>
                </span>
              )}
              {commercialCode && (
                <span>
                  Коммерческий код: <b>{commercialCode}</b>
                </span>
              )}
              {category && (
                <span>
                  Категория: <b>{category.title}</b>
                </span>
              )}
            </div>
          </div>
          <div className={styles.documentation}>
            <h3>Техническая документация</h3>
            <div className={styles.holder}>
              {!loadingDocuments ? (
                <>
                  <div className={styles.list}>
                    {electricalSchemeLinks.map((item) => (
                      <span key={item.url}>
                        <Print />
                        <a href={item.url} rel="noreferrer" target="_blank">
                          Электрическая-схема.pdf
                        </a>{' '}
                        ({formatBits(item.size)})
                      </span>
                    ))}
                    {repInstructionLinks.map((item) => (
                      <span key={item.url}>
                        <Print />
                        <a href={item.url} rel="noreferrer" target="_blank">
                          Руководство по ремонту.pdf
                        </a>{' '}
                        ({formatBits(item.size)})
                      </span>
                    ))}
                    {techInfoLinks.map((item) => (
                      <span key={item.url}>
                        <Print />
                        <a href={item.url} rel="noreferrer" target="_blank">
                          Tехническая информация.pdf
                        </a>{' '}
                        ({formatBits(item.size)})
                      </span>
                    ))}
                  </div>
                  {explodedViews &&
                  explodedViews.length &&
                  explodedViews[0].s3Bucket &&
                  explodedViews[0].s3Key ? (
                    <Link to={'/scheme/' + id}>
                      Интерактивная взрыв-схема →
                    </Link>
                  ) : null}
                </>
              ) : (
                <p>Загрузка...</p>
              )}
            </div>
          </div>
        </div>
        <div className={styles.image_holder}>
          <div className={styles.image}>
            {image ? (
              <img
                src={
                  image?.s3Key
                    ? generateImageUrl(image?.s3Key)
                    : '/images/placeholderImage.png'
                }
                alt="image"
              />
            ) : null}
          </div>
          {date && <span>Дата создания: {date}</span>}
        </div>
      </div>
      {(extras && extras.length) || (parts && parts.length) ? (
        <div className={styles.list_holder}>
          <h3>Номенклатура</h3>
          <div className={styles.content_holder}>
            <div className={styles.switch_btns}>
              {parts && parts.length ? (
                <button
                  className={clsx({ [styles.active]: type === 'parts' })}
                  onClick={() => setType('parts')}
                >
                  Запчасти
                </button>
              ) : null}
              {extras && extras.length ? (
                <button
                  className={clsx({ [styles.active]: type === 'accessories' })}
                  onClick={() => setType('accessories')}
                >
                  Аксессуары
                </button>
              ) : null}
            </div>
            <span>
              <span>Показано</span> {(currentPage - 1) * limit + list.length} из{' '}
              {type === 'parts' && parts
                ? parts.length
                : type === 'accessories' && extras
                  ? extras.length
                  : list.length}
            </span>
          </div>

          <List list={list} type={type} />
          {(pages > 1 || (limit !== 20 && pages === 1)) && (
            <Pagination
              count={pages}
              currentPage={currentPage}
              updatePage={updatePage}
              updateLimit={updateLimit}
              limit={limit}
              remainder={remainder}
              showMore={showMore}
            />
          )}
        </div>
      ) : null}
      <div className={styles.download_catalog}>
        <span>Инструменты SEB для ремонта и сервисного обслуживания</span>
        <span>
          <Print />
          <a
            href="https://6e8ee6a6-c00b-40bb-a4ec-44a1c22bfeef.selstorage.ru/5aaa669e-9d46-4d24-a023-a3296c90bbb2.pdf"
            target="_blank"
            rel="noreferrer"
            className={styles.link}
          >
            Каталог.pdf
          </a>{' '}
          (672 КБ)
        </span>
      </div>
    </div>
  );
};

export default Model;
